import { routePaths } from "../constants/routes";

export const manualClusterInternalLinks = [
  { label: "Learn Automation Testing", to: routePaths.automationTesting },
  { label: "Learn API Testing", to: routePaths.apiTesting },
  { label: "Testing Interview Questions", to: routePaths.interviewQuestions },
];

const defaultAdvantages = [
  "Helps testers understand the product from the user's point of view before automation starts.",
  "Finds usability, requirement, workflow, and business logic defects that tools may miss.",
  "Improves communication between QA, developers, product owners, and business teams.",
  "Builds a strong foundation for API testing, automation testing, and interview preparation.",
];

const defaultDisadvantages = [
  "Manual testing can take more time when the same regression suite must be repeated often.",
  "Results may vary if test cases, test data, and expected results are not clearly documented.",
  "Large applications need a smart mix of manual testing and automation to keep releases fast.",
];

function faqFor(keyword) {
  return [
    {
      question: `Is ${keyword} important for freshers?`,
      answer: `Yes. ${keyword} is important for freshers because most QA interviews start with manual testing fundamentals, real examples, and scenario-based questions.`,
    },
    {
      question: `Do I need coding to learn ${keyword}?`,
      answer: "No. You can start manual testing without coding. Coding becomes useful later when you move into automation testing or API automation.",
    },
    {
      question: `How should I practice ${keyword}?`,
      answer: "Pick a real application such as login, ecommerce, booking, or banking, then write scenarios, test cases, expected results, and defect reports.",
    },
  ];
}

function makeManualPage({
  slug,
  title,
  description,
  keyword,
  what,
  why,
  types,
  example,
  questions,
}) {
  return {
    slug,
    path: `/${slug}`,
    eyebrow: "Manual Testing SEO Cluster",
    title,
    description,
    keyword,
    sections: [
      {
        id: "what-is-it",
        heading: `What is ${keyword}?`,
        paragraphs: what,
      },
      {
        id: "why-important",
        heading: "Why important",
        paragraphs: why,
      },
      {
        id: "types",
        heading: "Types",
        list: types,
      },
      {
        id: "example",
        heading: "Example",
        paragraphs: example,
      },
      {
        id: "advantages",
        heading: "Advantages",
        list: defaultAdvantages,
      },
      {
        id: "disadvantages",
        heading: "Disadvantages",
        list: defaultDisadvantages,
      },
      {
        id: "interview-questions",
        heading: "Interview Questions",
        list: questions,
      },
      {
        id: "faq",
        heading: "FAQ",
        faqs: faqFor(keyword),
      },
    ],
  };
}

export const manualTestingClusterPages = [
  makeManualPage({
    slug: "what-is-manual-testing",
    title: "What is Manual Testing? Complete Beginner Guide",
    description: "Learn what manual testing is, why it matters, types, examples, advantages, disadvantages, interview questions, and FAQs for beginners.",
    keyword: "Manual Testing",
    what: [
      "Manual testing is the process of checking a software application by using it like a real user and comparing the actual result with the expected result. A manual tester studies requirements, prepares scenarios, writes test cases, executes them, reports defects, and confirms fixes after retesting.",
      "It is called manual because the tester performs the validation without writing automation scripts. Manual testing is especially useful when the product is new, requirements are changing, usability needs human judgement, or the team wants fast exploratory feedback.",
    ],
    why: [
      "Manual testing is important because not every defect is technical. Many issues are related to confusing user flows, missing validation, unclear error messages, broken business rules, or poor user experience.",
      "For beginners, manual testing builds the base thinking required for automation testing, API testing, test planning, defect reporting, and QA interviews.",
    ],
    types: ["Functional testing", "Regression testing", "Smoke testing", "Sanity testing", "Exploratory testing", "Usability testing", "Acceptance testing"],
    example: [
      "Example: To test a login page manually, a tester checks valid login, invalid password, blank username, invalid email format, locked account, forgot password, remember me, and logout behavior.",
      "The tester records expected results, actual results, screenshots, browser/device details, and defect severity if any flow fails.",
    ],
    questions: ["What is manual testing?", "Why is manual testing needed?", "What are the types of manual testing?", "How do you test a login page manually?", "Difference between manual and automation testing?"],
  }),
  makeManualPage({
    slug: "manual-testing-process",
    title: "Manual Testing Process Step by Step",
    description: "Understand the manual testing process from requirement analysis to test planning, test case design, execution, defect reporting, retesting, and closure.",
    keyword: "Manual Testing Process",
    what: [
      "The manual testing process is a step-by-step QA workflow used to verify application quality before release. It starts with understanding requirements and ends with test closure and quality reporting.",
      "A good process helps testers avoid random testing. It gives structure to what should be tested, how it should be tested, who will test it, and when testing can be considered complete.",
    ],
    why: [
      "A clear manual testing process reduces missed test coverage and helps the team make better release decisions.",
      "It is also important in interviews because freshers are often asked to explain how they would test a feature from requirement to closure.",
    ],
    types: ["Requirement analysis", "Test planning", "Test scenario design", "Test case writing", "Test execution", "Defect reporting", "Retesting and regression", "Test closure"],
    example: [
      "Example: For an ecommerce checkout feature, QA first studies requirements, identifies payment and cart scenarios, writes test cases, prepares test data, executes cases, logs defects, retests fixes, runs regression, and shares a test summary report.",
    ],
    questions: ["What are the steps in manual testing?", "What happens in requirement analysis?", "What is test closure?", "When do you perform retesting?", "Why is regression needed after defect fixes?"],
  }),
  makeManualPage({
    slug: "manual-testing-examples",
    title: "Manual Testing Examples with Real-Time Scenarios",
    description: "Practice manual testing with real-time examples for login, registration, ecommerce cart, checkout, search, filters, payment, and profile update flows.",
    keyword: "Manual Testing Examples",
    what: [
      "Manual testing examples are practical application flows used to understand how QA validates real software. Instead of memorizing definitions, learners test common features such as login, registration, search, cart, checkout, and payment.",
      "Examples help beginners convert requirements into test scenarios, test cases, expected results, and defect reports.",
    ],
    why: [
      "Real-time examples are important because interviews often ask 'How will you test this page?' rather than only definitions.",
      "Practicing examples improves observation, requirement thinking, negative testing, boundary testing, and defect writing.",
    ],
    types: ["Login examples", "Registration examples", "Search examples", "Ecommerce examples", "Payment examples", "Profile update examples"],
    example: [
      "Login example: Test valid login, wrong password, unregistered email, blank fields, password masking, forgot password, remember me, account lock, and logout.",
      "Cart example: Add product, remove product, update quantity, apply coupon, check tax/shipping, verify total price, and proceed to checkout.",
    ],
    questions: ["Give manual testing examples.", "How do you test registration?", "How do you test ecommerce checkout?", "How do you test search functionality?", "What negative cases can you write for login?"],
  }),
  makeManualPage({
    slug: "manual-testing-test-cases",
    title: "Manual Testing Test Cases with Examples",
    description: "Learn how to write manual testing test cases with test case format, fields, examples, positive cases, negative cases, and interview questions.",
    keyword: "Manual Testing Test Cases",
    what: [
      "Manual testing test cases are documented steps that tell a tester what to test, how to test it, what data to use, and what result is expected.",
      "A test case usually includes test case ID, title, precondition, test steps, test data, expected result, actual result, status, priority, and comments.",
    ],
    why: [
      "Test cases are important because they make testing repeatable, reviewable, and measurable.",
      "Good test cases help teams avoid confusion during regression testing, user acceptance testing, defect retesting, and release sign-off.",
    ],
    types: ["Positive test cases", "Negative test cases", "Boundary value cases", "UI validation cases", "Business rule cases", "Regression cases"],
    example: [
      "Example: Test case title: Verify user can login with valid credentials. Steps: open login page, enter registered email, enter valid password, click login. Expected result: user should navigate to dashboard.",
      "Negative example: Enter invalid password for a registered email. Expected result: login should fail and a clear error message should appear.",
    ],
    questions: ["What is a test case?", "What fields are included in a test case?", "Difference between test case and test scenario?", "How do you write negative test cases?", "What is expected result?"],
  }),
  makeManualPage({
    slug: "manual-testing-checklist",
    title: "Manual Testing Checklist for Web Applications",
    description: "Use this manual testing checklist for UI, functionality, forms, validation, compatibility, usability, security basics, and regression testing.",
    keyword: "Manual Testing Checklist",
    what: [
      "A manual testing checklist is a quick reference list that helps testers verify important areas of an application without missing common issues.",
      "It is not a replacement for detailed test cases, but it is useful during smoke testing, exploratory testing, release checks, and fast reviews.",
    ],
    why: [
      "Checklists are important because testers often work under time pressure. A checklist keeps attention on critical areas such as login, forms, validation, navigation, data saving, error handling, and responsiveness.",
      "For beginners, a checklist provides confidence when testing a page for the first time.",
    ],
    types: ["UI checklist", "Form validation checklist", "Functional checklist", "Compatibility checklist", "Security basics checklist", "Regression checklist"],
    example: [
      "Example checklist for a form: mandatory fields, optional fields, invalid email, phone length, duplicate record, submit button state, success message, error message, keyboard tab order, mobile layout, and data persistence.",
    ],
    questions: ["What is a manual testing checklist?", "When do you use a checklist?", "Checklist vs test case?", "What should be checked before release?", "How do you create a web testing checklist?"],
  }),
  makeManualPage({
    slug: "manual-testing-life-cycle",
    title: "Manual Testing Life Cycle Explained",
    description: "Learn manual testing life cycle phases including requirement analysis, planning, test design, environment setup, execution, defect reporting, and closure.",
    keyword: "Manual Testing Life Cycle",
    what: [
      "Manual testing life cycle is the sequence of QA activities followed to test software manually. It is closely related to STLC and helps teams organize testing from requirement understanding to final closure.",
      "Each phase has a clear purpose: understand scope, plan effort, design test cases, execute tests, report defects, verify fixes, and summarize quality.",
    ],
    why: [
      "The life cycle is important because testing without phases becomes unorganized and hard to track.",
      "Understanding this flow helps freshers answer STLC questions and explain how QA fits into real project delivery.",
    ],
    types: ["Requirement phase", "Planning phase", "Design phase", "Execution phase", "Defect phase", "Closure phase"],
    example: [
      "Example: In a sprint, QA reviews user stories, asks requirement questions, writes test scenarios, prepares test data, executes test cases after build deployment, reports bugs, retests fixes, runs regression, and shares sign-off notes.",
    ],
    questions: ["What is manual testing life cycle?", "Is manual testing life cycle same as STLC?", "What is test closure?", "What happens during test execution?", "What is defect retesting?"],
  }),
  makeManualPage({
    slug: "manual-testing-vs-automation-testing",
    title: "Manual Testing vs Automation Testing",
    description: "Compare manual testing and automation testing with differences, examples, advantages, disadvantages, and interview questions for QA learners.",
    keyword: "Manual Testing vs Automation Testing",
    what: [
      "Manual testing is performed by a human tester without scripts, while automation testing uses tools and code to execute test cases automatically.",
      "Both are important. Manual testing is best for new features, exploratory testing, usability, and requirement validation. Automation testing is best for stable, repetitive, and high-volume regression tests.",
    ],
    why: [
      "Understanding the difference helps QA learners choose the right testing approach for each situation.",
      "Interviewers often ask this topic to check whether a candidate understands practical testing strategy, not just tool names.",
    ],
    types: ["Manual exploratory testing", "Manual functional testing", "Automated regression testing", "Automated smoke testing", "API automation", "UI automation"],
    example: [
      "Example: A new checkout design should first be tested manually for flow, usability, validation, and edge cases. Once stable, critical checkout regression cases can be automated.",
    ],
    questions: ["Difference between manual and automation testing?", "Can automation replace manual testing?", "When should you automate?", "Which testing is better for UI changes?", "Why is manual testing still needed?"],
  }),
  makeManualPage({
    slug: "manual-testing-interview-questions",
    title: "Manual Testing Interview Questions and Answers",
    description: "Prepare manual testing interview questions on SDLC, STLC, test cases, test scenarios, defect life cycle, severity, priority, and regression testing.",
    keyword: "Manual Testing Interview Questions",
    what: [
      "Manual testing interview questions are asked to check whether a candidate understands QA fundamentals, test design, defect reporting, project workflow, and real-time testing scenarios.",
      "Strong answers should include a simple definition, practical example, and a clear explanation of when the concept is used.",
    ],
    why: [
      "Manual testing interviews usually decide whether a fresher understands the base of QA before moving into tools or automation.",
      "Practicing questions also helps learners organize concepts like SDLC, STLC, test cases, scenarios, severity, priority, smoke testing, and regression testing.",
    ],
    types: ["Definition questions", "Difference questions", "Scenario questions", "Defect questions", "Process questions", "Project questions"],
    example: [
      "Example answer: Severity means impact of a defect on the application. Priority means how soon it should be fixed. A logo spelling issue can be low severity but high priority if it is on the homepage.",
    ],
    questions: ["What is manual testing?", "What is SDLC and STLC?", "Test case vs test scenario?", "Severity vs priority?", "Smoke vs sanity testing?", "What is bug life cycle?", "How do you test a login page?"],
  }),
  makeManualPage({
    slug: "manual-testing-for-beginners",
    title: "Manual Testing for Beginners",
    description: "Start manual testing from scratch with beginner-friendly explanations, process, examples, test cases, checklists, interview questions, and FAQs.",
    keyword: "Manual Testing for Beginners",
    what: [
      "Manual testing for beginners means learning how to verify software quality step by step without depending on automation tools first.",
      "A beginner should start with software testing basics, SDLC, STLC, test scenarios, test cases, defect reporting, testing types, and real-time examples.",
    ],
    why: [
      "Manual testing is the foundation of QA. Without understanding requirements, expected results, user flows, and defects, automation scripts become weak.",
      "For career growth, beginners should learn manual testing first, then add API testing, SQL, automation testing, and interview preparation.",
    ],
    types: ["Functional testing", "Smoke testing", "Regression testing", "Exploratory testing", "UI testing", "Acceptance testing"],
    example: [
      "Beginner practice example: Choose a login page and write test scenarios, positive test cases, negative test cases, boundary cases, expected results, and possible defects.",
      "Next, test a registration form, ecommerce cart, search feature, and profile update page to build real project confidence.",
    ],
    questions: ["How do I start manual testing?", "What should a beginner learn first?", "Do manual testers need SQL?", "How do I practice test cases?", "Can I get a QA job with manual testing?"],
  }),
];

export function getManualTestingClusterPage(slug) {
  return manualTestingClusterPages.find((page) => page.slug === slug);
}
